import {createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useReducer} from "react";
import ShoppingListData = App.Data.ShoppingListData;
import ShoppingListItemData = App.Data.ShoppingListItemData;

interface ShoppingListState {
  uuid: string;
  name: string;
  slug: string;
  items: ShoppingListItemData[];
}

type ShoppingListAction =
  | { type: "RESET"; payload?: ShoppingListData }
  | { type: "REPLACE_ITEMS"; items: ShoppingListItemData[] }
  | { type: "ADD_ITEM"; item: ShoppingListItemData }
  | { type: "REMOVE_ITEM"; itemUuid: string }
  | { type: "CLEAR" };

interface ShoppingListContextValue {
  shoppingList: ShoppingListState;
  list: ShoppingListState;
  uuid: string;
  items: ShoppingListItemData[];
  addRecipe: (items: ShoppingListItemData[]) => void;
  addMealPlan: (items: ShoppingListItemData[]) => void;
  addItem: (item: ShoppingListItemData) => void;
  removeItem: (item: ShoppingListItemData) => void;
  clearList: () => void;
}

const createShoppingListState = (list?: ShoppingListData): ShoppingListState => ({
  uuid: list?.uuid ?? "",
  name: list?.name ?? "",
  slug: list?.slug ?? "",
  items: list?.items ? [...list.items] : [],
});

const reducer = (state: ShoppingListState, action: ShoppingListAction): ShoppingListState => {
  switch (action.type) {
    case "RESET": {
      const list = action.payload;
      return createShoppingListState(list);
    }
    case "REPLACE_ITEMS":
      return {
        ...state,
        items: [...action.items],
      };
    case "ADD_ITEM":
      return {
        ...state,
        items: [...state.items, action.item],
      };
    case "REMOVE_ITEM":
      return {
        ...state,
        items: state.items.filter((item) => item.uuid !== action.itemUuid),
      };
    case "CLEAR":
      return {
        ...state,
        items: [],
      };
    default:
      return state;
  }
};

const ShoppingListContext = createContext<ShoppingListContextValue | undefined>(undefined);

const createSignature = (list?: ShoppingListData): string => {
  if (!list) {
    return "";
  }

  const itemSignature = (list.items ?? [])
    .map((item) => item.uuid)
    .join("|");

  return [list.uuid, itemSignature].join(":");
};

export const ShoppingListProvider = ({
  initialState,
  children,
}: {
  initialState?: ShoppingListData;
  children: ReactNode;
}) => {
  const [state, dispatch] = useReducer(reducer, initialState, createShoppingListState);

  const signature = useMemo(() => createSignature(initialState), [initialState]);

  useEffect(() => {
    if (initialState) {
      dispatch({ type: "RESET", payload: initialState });
    } else {
      dispatch({ type: "RESET" });
    }
  }, [signature]);

  const replaceItems = useCallback((items: ShoppingListItemData[] = []) => {
    dispatch({ type: "REPLACE_ITEMS", items });
  }, []);

  const addItem = useCallback((item: ShoppingListItemData) => {
    dispatch({ type: "ADD_ITEM", item });
  }, []);

  const removeItem = useCallback((item: ShoppingListItemData) => {
    dispatch({ type: "REMOVE_ITEM", itemUuid: item.uuid });
  }, []);

  const clearList = useCallback(() => {
    dispatch({ type: "CLEAR" });
  }, []);

  const value = useMemo<ShoppingListContextValue>(() => ({
    shoppingList: state,
    list: state,
    uuid: state.uuid,
    items: state.items,
    addRecipe: replaceItems,
    addMealPlan: replaceItems,
    addItem,
    removeItem,
    clearList,
  }), [state, replaceItems, addItem, removeItem, clearList]);

  return (
    <ShoppingListContext.Provider value={value}>
      {children}
    </ShoppingListContext.Provider>
  );
};

export const useShoppingList = (): ShoppingListContextValue => {
  const context = useContext(ShoppingListContext);

  if (!context) {
    throw new Error("useShoppingList must be used within a ShoppingListProvider");
  }

  return context;
};
